import React, { useEffect, useState } from 'react';
import { Gift, CreditCard, Star, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import loyaltyService from '../../services/loyaltyService';

/* The bill modal's rewards drawer: pin a member to the bill, take a voucher,
   spend points as money (CURRENCY scheme only), pay from a gift card. Every
   apply lands a redemption row against the ORDER on the server and then asks
   the parent to re-read the bill — the deduction lines the modal shows come
   back from the order detail, never from local arithmetic, so the screen can
   never disagree with what was actually recorded. */

// Mirror of the server's voucher_value_on, for the preview line only.
const voucherValueOn = (v, billTotal) => {
  const bill = Number(billTotal) || 0;
  if (v.kind === 'PERCENT_OFF') return (bill * (Number(v.percent_value) || 0)) / 100;
  if (v.kind === 'AMOUNT_OFF') return Math.min(Number(v.amount_value) || 0, bill);
  return 0; // FREE_DISH / EXPERIENCE — the reward is the thing itself
};

const digitsOf = (s) => String(s || '').replace(/\D/g, '');

const BillRewardsPanel = ({
  orderId, customerPhone, grossTotal, due, locationId, money, onApplied,
}) => {
  // false = hide for good (programme off, not seeded, or no permission).
  const [program, setProgram] = useState(null);
  const [member, setMember] = useState(null);
  const [projected, setProjected] = useState('');

  const [memberQ, setMemberQ] = useState('');
  const [memberResults, setMemberResults] = useState(null);
  const [memberErr, setMemberErr] = useState('');

  const [vCode, setVCode] = useState('');
  const [voucher, setVoucher] = useState(null);
  const [vErr, setVErr] = useState('');

  const [gcCode, setGcCode] = useState('');
  const [giftCard, setGiftCard] = useState(null);
  const [gcAmount, setGcAmount] = useState('');
  const [gcErr, setGcErr] = useState('');

  const [ptsAmount, setPtsAmount] = useState('');
  const [ptsErr, setPtsErr] = useState('');
  const [busy, setBusy] = useState('');

  const fmt = money || ((n) => (Number(n) || 0).toFixed(2));

  useEffect(() => {
    let alive = true;
    loyaltyService.getProgram()
      .then((p) => { if (alive) setProgram(p && p.is_enabled ? p : false); })
      .catch(() => { if (alive) setProgram(false); });
    return () => { alive = false; };
  }, []);

  // The order already knows its guest (QR sitting, booking): find the
  // membership on the same phone so the panel opens on "Sam · 1,250 pts"
  // instead of an empty search box.
  useEffect(() => {
    const digits = digitsOf(customerPhone);
    if (!program || program === false || member || digits.length < 4) return;
    let alive = true;
    loyaltyService.lookup(digits)
      .then((r) => {
        const hit = (r?.matches || []).find(
          (m) => digitsOf(m.phone).endsWith(digits.slice(-8)));
        if (alive && hit) setMember(hit);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, customerPhone]);

  if (program === null || program === false) return null;

  const run = async (tag, fn, setErr) => {
    setBusy(tag); setErr('');
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  const searchMembers = () => run('search', async () => {
    setMemberResults(null);
    const r = await loyaltyService.lookup(memberQ.trim());
    setMemberResults(r?.matches || []);
  }, setMemberErr);

  const attach = (acc) => run('attach', async () => {
    const r = await loyaltyService.attachOrder(orderId, acc.id);
    setMember(r?.account || acc);
    setProjected(r?.projected_points || '');
    setMemberResults(null);
    setMemberQ('');
  }, setMemberErr);

  const checkVoucher = () => run('vcheck', async () => {
    setVoucher(null);
    const v = await loyaltyService.voucherLookup(vCode.trim());
    if (v.status !== 'ACTIVE') throw new Error(`This voucher is ${v.status.toLowerCase()}.`);
    setVoucher(v);
  }, setVErr);

  const applyVoucher = () => run('vapply', async () => {
    await loyaltyService.redeemVoucher({
      code: voucher.code, order_id: orderId, bill_total: grossTotal,
      location_id: locationId || undefined,
    });
    setVoucher(null); setVCode('');
    await onApplied();
  }, setVErr);

  const checkGiftCard = () => run('gcheck', async () => {
    setGiftCard(null);
    const g = await loyaltyService.giftCardLookup(gcCode.trim());
    if (g.status !== 'ACTIVE') throw new Error(`This gift card is ${g.status.toLowerCase()}.`);
    setGiftCard(g);
    setGcAmount(String(Math.min(Number(g.amount_remaining) || 0, due).toFixed(2)));
  }, setGcErr);

  const applyGiftCard = () => run('gapply', async () => {
    await loyaltyService.redeemGiftCard({
      code: giftCard.code, amount: gcAmount, order_id: orderId,
      location_id: locationId || undefined,
    });
    setGiftCard(null); setGcCode(''); setGcAmount('');
    await onApplied();
  }, setGcErr);

  const redeemPoints = () => run('points', async () => {
    const r = await loyaltyService.redeemCurrency(member.id, {
      amount: ptsAmount, order_id: orderId, location_id: locationId || undefined,
    });
    setMember((m) => (m ? { ...m, points_balance: r.balance_after } : m));
    setPtsAmount('');
    await onApplied();
  }, setPtsErr);

  const pointsAsMoney = program.points_are_money && member;
  const memberPointsValue = member?.points_value != null
    ? Number(member.points_value) : null;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
        <Gift size={13} className="text-teal-700" /> {program.name || 'Rewards'} &amp; gift cards
      </p>

      {/* The member on this bill — found from the order's phone, or searched. */}
      {member ? (
        <div className="text-sm text-gray-800">
          <span className="font-medium">{member.name}</span>
          <span className="text-gray-500"> · {member.membership_number}</span>
          <span className="text-gray-500"> · {Number(member.points_balance).toLocaleString()} pts</span>
          {member.tier?.name ? <span className="text-gray-500"> · {member.tier.name}</span> : null}
          {projected && Number(projected) > 0 && (
            <p className="text-xs text-emerald-700 mt-0.5">
              Will earn {Number(projected).toLocaleString()} points when this bill settles.
            </p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <Input
              value={memberQ}
              onChange={(e) => setMemberQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchMembers(); }}
              placeholder="Member — phone, name or number"
              className="h-8 text-sm"
            />
            <Button type="button" variant="outline" className="h-8 px-2.5"
              disabled={busy === 'search' || memberQ.trim().length < 3} onClick={searchMembers}>
              <Search size={14} />
            </Button>
          </div>
          {memberResults && memberResults.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">No member found — they can join from the QR menu.</p>
          )}
          {(memberResults || []).map((m) => (
            <button key={m.id} type="button" disabled={busy === 'attach'}
              onClick={() => attach(m)}
              className="mt-1 w-full text-left text-sm rounded border border-gray-200 bg-white px-2 py-1 hover:border-teal-500">
              {m.name} <span className="text-gray-500">· {m.phone} · {Number(m.points_balance).toLocaleString()} pts</span>
            </button>
          ))}
          {memberErr && <p className="mt-1 text-xs text-red-600">{memberErr}</p>}
        </div>
      )}

      {/* Points straight off the bill — CURRENCY scheme only. */}
      {pointsAsMoney && (
        <div>
          <div className="flex items-center gap-2">
            <Star size={14} className="shrink-0 text-amber-500" />
            <Input type="number" min="0" step="0.01" value={ptsAmount}
              onChange={(e) => setPtsAmount(e.target.value)}
              placeholder={memberPointsValue != null
                ? `Points — up to ${fmt(Math.min(memberPointsValue, due))}`
                : 'Amount to pay with points'}
              className="h-8 text-sm" />
            <Button type="button" variant="outline" className="h-8"
              disabled={busy === 'points' || !(Number(ptsAmount) > 0)} onClick={redeemPoints}>
              Redeem
            </Button>
          </div>
          {ptsErr && <p className="mt-1 text-xs text-red-600">{ptsErr}</p>}
        </div>
      )}

      {/* A voucher read out at the table: check first (single-use — a blind
          apply would burn a mistyped code's real neighbour), then apply. */}
      <div>
        <div className="flex items-center gap-2">
          <Gift size={14} className="shrink-0 text-teal-700" />
          <Input value={vCode} onChange={(e) => setVCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') checkVoucher(); }}
            placeholder="Voucher code" className="h-8 text-sm" />
          <Button type="button" variant="outline" className="h-8"
            disabled={busy === 'vcheck' || !vCode.trim()} onClick={checkVoucher}>
            Check
          </Button>
        </div>
        {voucher && (
          <div className="mt-1 flex items-center justify-between gap-2 text-sm rounded border border-teal-200 bg-teal-50 px-2 py-1">
            <span className="min-w-0 truncate">
              {voucher.title}
              <span className="text-gray-500"> · {voucher.member_name}</span>
            </span>
            <Button type="button" className="h-7 shrink-0 bg-teal-700 hover:bg-teal-800 text-white"
              disabled={busy === 'vapply'} onClick={applyVoucher}>
              {voucherValueOn(voucher, grossTotal) > 0
                ? `Take off ${fmt(voucherValueOn(voucher, grossTotal))}`
                : 'Apply to bill'}
            </Button>
          </div>
        )}
        {vErr && <p className="mt-1 text-xs text-red-600">{vErr}</p>}
      </div>

      {/* Gift card — payment, not a discount; the server refuses rather than
          clamps, so the default amount offered is what the card can cover. */}
      <div>
        <div className="flex items-center gap-2">
          <CreditCard size={14} className="shrink-0 text-indigo-600" />
          <Input value={gcCode} onChange={(e) => setGcCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') checkGiftCard(); }}
            placeholder="Gift card code" className="h-8 text-sm" />
          <Button type="button" variant="outline" className="h-8"
            disabled={busy === 'gcheck' || !gcCode.trim()} onClick={checkGiftCard}>
            Check
          </Button>
        </div>
        {giftCard && (
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="text-gray-600 shrink-0">
              Holds {fmt(giftCard.amount_remaining)} —
            </span>
            <Input type="number" min="0" step="0.01" value={gcAmount}
              onChange={(e) => setGcAmount(e.target.value)} className="h-8 text-sm" />
            <Button type="button" className="h-8 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={busy === 'gapply' || !(Number(gcAmount) > 0)} onClick={applyGiftCard}>
              Pay
            </Button>
          </div>
        )}
        {gcErr && <p className="mt-1 text-xs text-red-600">{gcErr}</p>}
      </div>
    </div>
  );
};

export default BillRewardsPanel;
